import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import {
  AutoComplete,
  Breadcrumb,
  Button,
  Checkbox,
  Col,
  Collapse,
  DatePicker,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Spin,
  Table,
  Tooltip,
  Upload,
  message,
} from "antd";
import {
  DeleteOutlined,
  PercentageOutlined,
  SaveOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { debounce } from "lodash";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { getRequest, patchRequest, postRequest } from "../../axios";
import { authUserData } from "../../utils/Helper";
import ButtonField from "../../common/htmlComponents/ButtonField";
import {
  ErrorNotificationMsg,
  ShowErrorMsg,
  SuccessNotificationMsg,
} from "../../utils/Notifications";
import { ReactComponent as Vector } from "../../image/Vector.svg";
import { ReactComponent as AddressLine } from "../../image/AddressLine.svg";
import { ReactComponent as Email } from "../../image/Email.svg";
import { ReactComponent as Name } from "../../image/Name.svg";
import { ReactComponent as IdcardOutlined } from "../../image/IdcardOutlined.svg";
import { ReactComponent as ZipCode } from "../../image/ZipCode.svg";
import { ReactComponent as FileAlt } from "../../image/FileAlt.svg";
import VerifyOtpModal from "../../authentication/VerifyOtpModel";
import PhoneOtpVerify from "../../authentication/PhoneOtpVerify";
import verifybtn from "../../image/verifybtn.png";

const { Panel } = Collapse;
const { Option } = Select;

const normPhone = (raw) => {
  if (raw == null || raw === "") return "";
  const digits = String(raw).replace(/\D/g, "");
  return digits;
};

const formatPhoneForInput = (raw) => {
  const digits = normPhone(raw);
  if (!digits) return "";
  if (digits.length === 10) return `91${digits}`;
  return digits;
};

const toTitleCase = (v) =>
  String(v || "")
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

const normAddressPart = (value) => String(value ?? "").trim().toLowerCase();

/** Infer "same as registered" when API omits the flag but addresses match. */
const areTradingAndRegisteredAddressesSame = (v) => {
  if (!v || typeof v !== "object") return false;

  const textFields = [
    ["registered_business_address_building", "trading_address_building"],
    ["registered_business_address_area", "trading_address_area"],
    ["registered_business_address_landmark", "trading_address_landmark"],
    ["registered_business_address_state", "trading_address_state"],
    ["registered_business_address_city", "trading_address_city"],
    ["registered_business_address_pincode_number", "trading_address_pincode_number"],
  ];

  const textMatches = textFields.every(
    ([registeredKey, tradingKey]) =>
      normAddressPart(v[registeredKey]) === normAddressPart(v[tradingKey]),
  );

  const regPin = v.registered_business_address_pincode;
  const tradPin = v.trading_address_pincode;
  const pinMatches =
    regPin == null && tradPin == null
      ? true
      : String(regPin ?? "") === String(tradPin ?? "");

  return textMatches && pinMatches;
};

const getPincodeListFromResponse = (res) => {
  const list = res?.data?.results?.data ?? res?.data?.data ?? [];
  return Array.isArray(list) ? list : [];
};

/** baseKey: registered_business_address | trading_address | billing */
const setAddressPincodeFromRow = (form, baseKey, row) => {
  if (!row) return;
  const { id, state_name, city_name } = row;
  const num = String(row.pincode_number ?? row.pincode ?? "");

  if (baseKey === "billing") {
    form.setFieldsValue({
      billing_pincode: id ?? null,
      billing_pincode_number: num,
      billing_city: city_name ?? "",
      billing_state: state_name ?? "",
    });
    return;
  }

  form.setFieldsValue({
    [`${baseKey}_pincode`]: id ?? null,
    [`${baseKey}_pincode_number`]: num,
    [`${baseKey}_city`]: city_name ?? "",
    [`${baseKey}_state`]: state_name ?? "",
  });
};

const buildVendorFormDataPayload = (values, { CompanyId } = {}) => {
  console.log(values)
  const phone = normPhone(values.phone);
  const billingPhone = normPhone(values.billing_phone);

  const bankGstn = Array.isArray(values.bank_gstin)
    ? values.bank_gstin[0] ?? null
    : values.bank_gstin ?? null;

  return {
    vendor_registered_name: values.vendor_registered_name
      ? toTitleCase(values.vendor_registered_name)
      : null,
    person_name: values.person_name ?? null,
    designation: values.designation ?? null,
    gst_no: values.gst_no ?? null,
    udyam_aadhaar_no: values.udyam_aadhaar_no ?? null,
    email: values.email ?? null,
    phone: phone || null,
    vendor_trade_name: values.vendor_trade_name ?? null,

    email_verified: !!values.email_verified,
    phone_verified: !!values.phone_verified,
    gst_no_verified: !!values.gst_no_verified,
    udyam_aadhaar_no_verified: !!values.udyam_aadhaar_no_verified,

    registered_business_address_building:
      values.registered_business_address_building ?? null,
    registered_business_address_area:
      values.registered_business_address_area ?? null,
    registered_business_address_landmark:
      values.registered_business_address_landmark ?? null,
    registered_business_address_state:
      values.registered_business_address_state ?? null,
    registered_business_address_city:
      values.registered_business_address_city ?? null,
    registered_business_address_pincode:
      values.registered_business_address_pincode ?? null,
    registered_business_address_pincode_number:
      values.registered_business_address_pincode_number ?? null,

    trading_address_building: values.trading_address_building ?? null,
    trading_address_area: values.trading_address_area ?? null,
    trading_address_landmark: values.trading_address_landmark ?? null,
    trading_address_state: values.trading_address_state ?? null,
    trading_address_city: values.trading_address_city ?? null,
    trading_address_pincode: values.trading_address_pincode ?? null,
    trading_address_pincode_number:
      values.trading_address_pincode_number ?? null,

    same_as_registered_business_address:
      !!values.same_as_registered_business_address,

    vendor_company_code: {
      id: values.vendor_company_code_id ?? null,
      company: CompanyId ?? null,
      vendor_code_by_company: values.vendor_code_by_company ?? null,
    },

    suppliers_billing_details: {
      id: values.suppliers_billing_details_id ?? null,
      company: CompanyId ?? null,
      category_tree: null,
      gst_for_billing: values.billing_gst_for_billing ?? null,
      address_line_1: values.billing_address_line_1 ?? null,
      address_line_2: values.billing_address_line_2 ?? null,
      person_name: values.billing_person_name ?? null,
      landmark: values.billing_landmark ?? null,
      email: values.billing_email ?? null,
      mobile_number: billingPhone || null,
      pincode: values.billing_pincode ?? null,
      pincode_number: values.billing_pincode_number ?? null,
      city: values.billing_city ?? null,
      state: values.billing_state ?? null,
    },

    bankdetails:
    {
      id: values.bankdetails_id ?? null,
      company: CompanyId ?? null,
      account_holder_name: values.bank_account_holder_name ?? null,
      account_number: values.bank_account_number ?? null,
      bank_name: values.bank_name ?? null,
      branch_name: values.bank_branch_name ?? null,
      ifsc_code: values.bank_ifsc_code ?? null,
      gstn: bankGstn,
    },

    company_payment_terms: {
      id: values.company_payment_terms_id ?? null,
      company: CompanyId ?? null,
      days_to_pay: values.payment_days_to_pay ?? null,
      when_to_pay: values.payment_when_to_pay ?? null,
      when_to_calculate: values.payment_when_to_calculate ?? null,
      advance:
        values.payment_advance === "" || values.payment_advance == null
          ? 0
          : Number(values.payment_advance),
      payment_security_type: values.payment_security_type ?? null,
      bank_garrentee_value:
        values.payment_security_type === "Secure with Bank Garrentee"
          ? Number(values.bank_garrentee_value) || null
          : "",
      bank_guarantee_supply_limit_value:
        values.payment_security_type === "Secure with Bank Garrentee"
          ? Number(values.bank_guarantee_supply_limit_value) || null
          : "",
      bank_garrentee_open_date:
        values.payment_security_type === "Secure with Bank Garrentee" &&
          values.bank_garrentee_open_date
          ? moment(values.bank_garrentee_open_date).format("DD-MM-YY")
          : null,
      bank_garrentee_validity_complation_date:
        values.payment_security_type === "Secure with Bank Garrentee" &&
          values.bank_garrentee_validity_complation_date
          ? moment(values.bank_garrentee_validity_complation_date).format(
            "DD-MM-YY",
          )
          : null,
      lc_value:
        values.payment_security_type === "Secure with LC"
          ? Number(values.lc_value) || null
          : "",
      lc_supply_limit_value:
        values.payment_security_type === "Secure with LC"
          ? Number(values.lc_supply_limit_value) || null
          : "",
      lc_open_date:
        values.payment_security_type === "Secure with LC" &&
          values.lc_open_date
          ? moment(values.lc_open_date).format("DD-MM-YY")
          : null,
      lc_validity_complation_date:
        values.payment_security_type === "Secure with LC" &&
          values.lc_validity_complation_date
          ? moment(values.lc_validity_complation_date).format("DD-MM-YY")
          : null,
      other_payment_security_type: "",
    },
  };
};

/** GET detail may return nested records as an object or a single-item array. */
const pickNestedDetail = (...sources) => {
  for (const source of sources) {
    if (source == null) continue;
    if (Array.isArray(source)) {
      const first = source[0];
      if (first && typeof first === "object") return first;
      continue;
    }
    if (typeof source === "object") return source;
  }
  return {};
};

const mapVendorApiToForm = (v) => {
  if (!v || typeof v !== "object") return {};
  const bill = pickNestedDetail(
    v.suppliers_billing_details,
    v.billing_details,
  );
  const bank = pickNestedDetail(v.bankdetails, v.bank_details);
  const pay = pickNestedDetail(
    v.company_payment_terms,
    v.vendor_company,
    v.payment_terms,
  );
  const vcc = pickNestedDetail(v.vendor_company_code);
  return {
    gst_no: v.gst_no ?? "",
    udyam_aadhaar_no: v.udyam_aadhaar_no ?? v.udyam_number ?? "",
    vendor_registered_name:
      v.vendor_registered_name ?? v.legal_name ?? v.name ?? "",
    vendor_trade_name: v.vendor_trade_name ?? v.trade_name ?? "",
    vendor_code_by_company:
      vcc.vendor_code_by_company ?? v.vendor_code_by_company ?? "",
    person_name: v.person_name ?? "",
    designation: v.designation ?? "",
    email_verified: !!v.email_verified,
    phone_verified: !!v.phone_verified,
    gst_no_verified: !!v.gst_no_verified,
    udyam_aadhaar_no_verified: !!v.udyam_aadhaar_no_verified,
    email: v.email ?? "",
    phone: formatPhoneForInput(v.phone ?? v.mobile_number ?? ""),

    registered_business_address_building: v.registered_business_address_building ?? "",
    registered_business_address_area: v.registered_business_address_area ?? "",
    registered_business_address_landmark: v.registered_business_address_landmark ?? "",
    registered_business_address_pincode: v.registered_business_address_pincode ?? null,
    registered_business_address_pincode_number:
      v.registered_business_address_pincode_number ?? "",
    registered_business_address_city: v.registered_business_address_city ?? "",
    registered_business_address_state: v.registered_business_address_state ?? "",

    trading_address_building: v.trading_address_building ?? "",
    trading_address_area: v.trading_address_area ?? "",
    trading_address_landmark: v.trading_address_landmark ?? "",
    trading_address_pincode: v.trading_address_pincode ?? null,
    trading_address_pincode_number: v.trading_address_pincode_number ?? "",
    trading_address_city: v.trading_address_city ?? "",
    trading_address_state: v.trading_address_state ?? "",
    same_as_registered_business_address:
      !!v.same_as_registered_business_address ||
      areTradingAndRegisteredAddressesSame(v),

    billing_gst_for_billing: bill.gst_for_billing ?? bill.gst ?? v.gst_no ?? "",
    billing_address_line_1: bill.address_line_1 ?? "",
    billing_address_line_2: bill.address_line_2 ?? "",
    billing_person_name: bill.person_name ?? "",
    billing_landmark: bill.landmark ?? "",
    billing_email: bill.email ?? "",
    billing_phone: formatPhoneForInput(
      bill.mobile_number ?? bill.phone ?? "",
    ),
    billing_pincode: bill.pincode ?? null,
    billing_pincode_number: bill.pincode_number ?? "",
    billing_city: bill.city ?? "",
    billing_state: bill.state ?? "",

    bank_account_holder_name: bank.account_holder_name ?? "",
    bank_account_number: bank.account_number ?? "",
    bank_name: bank.bank_name ?? "",
    bank_branch_name: bank.branch_name ?? "",
    bank_ifsc_code: bank.ifsc_code ?? bank.ifsc ?? "",
    bank_gstin: bank.gstn
      ? String(bank.gstn)
      : bank.gstin
        ? String(bank.gstin)
        : undefined,

    payment_days_to_pay: pay.days_to_pay ?? "7 days",
    payment_when_to_pay: pay.when_to_pay ?? "Within",
    payment_when_to_calculate:
      pay.when_to_calculate ?? "From the date of invoice",
    payment_advance: pay.advance ?? 0,
    payment_security_type: pay.payment_security_type ?? "Open Credit",
    bank_garrentee_value: pay.bank_garrentee_value ?? "",
    bank_guarantee_supply_limit_value:
      pay.bank_guarantee_supply_limit_value ?? "",
    bank_garrentee_open_date: pay.bank_garrentee_open_date
      ? moment(pay.bank_garrentee_open_date, ["DD-MM-YY", moment.ISO_8601])
      : null,
    bank_garrentee_validity_complation_date:
      pay.bank_garrentee_validity_complation_date
        ? moment(pay.bank_garrentee_validity_complation_date, [
          "DD-MM-YY",
          moment.ISO_8601,
        ])
        : null,
    lc_value: pay.lc_value ?? "",
    lc_supply_limit_value: pay.lc_supply_limit_value ?? "",
    lc_open_date: pay.lc_open_date
      ? moment(pay.lc_open_date, ["DD-MM-YY", moment.ISO_8601])
      : null,
    lc_validity_complation_date: pay.lc_validity_complation_date
      ? moment(pay.lc_validity_complation_date, ["DD-MM-YY", moment.ISO_8601])
      : null,
    vendor_company_code_id: vcc.id ?? null,
    suppliers_billing_details_id: bill.id ?? null,
    bankdetails_id: bank.id ?? null,
    company_payment_terms_id: pay.id ?? null,
  };
};

const GeneralVendorAdd = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEdit = Boolean(editId);

  const [form] = Form.useForm();
  const [detailLoading, setDetailLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [registeredPinOptions, setRegisteredPinOptions] = useState([]);
  const [tradingPinOptions, setTradingPinOptions] = useState([]);
  const [billingPinOptions, setBillingPinOptions] = useState([]);
  const [billingPhoneInputKey, setBillingPhoneInputKey] = useState(0);

  const [docNameInput, setDocNameInput] = useState("");
  const [docFile, setDocFile] = useState(null);
  const [documentRows, setDocumentRows] = useState([]);

  const [btnLoadingVerifyGst, setBtnLoadingVerifyGst] = useState(false);
  const [verifyGstButtonView, setVerifyGstButtonView] = useState(true);
  const [verificationStatus, setVerificationStatus] = useState(null);

  const [btnLoadingAdhaar, setBtnLoadingAdhaar] = useState(false);
  const [verifyAdhaarButtonView, setVerifyAdhaarButtonView] = useState(true);
  const [verificationResultAdhaar, setVerificationResultAdhaar] =
    useState(null);

  const [btnLoadingEmail, setBtnLoadingEmail] = useState(false);
  const [verifyEmailButtonView, setVerifyEmailButtonView] = useState(true);
  const [verificationResultEmail, setVerificationResultEmail] =
    useState(null);

  const [btnLoadingPhone, setBtnLoadingPhone] = useState(false);
  const [verifyPhoneButtonView, setVerifyPhoneButtonView] = useState(true);
  const [verificationResultContact, setVerificationResultContact] =
    useState(null);

  const [otpContactState, setOtpContactState] = useState({
    email: "",
    phone: "",
    email_verified: false,
    phone_verified: false,
  });

  const patchOtpContactState = useCallback(
    (update) => {
      setOtpContactState((prev) => {
        const next =
          typeof update === "function" ? update(prev) : { ...prev, ...update };
        if (next.email_verified !== undefined) {
          form.setFieldsValue({ email_verified: next.email_verified });
        }
        if (next.phone_verified !== undefined) {
          form.setFieldsValue({ phone_verified: next.phone_verified });
        }
        return next;
      });
    },
    [form],
  );

  const [OTP, setOTP] = useState(null);
  const [OTPPhone, setOTPPhone] = useState(null);
  const [minutes, setMinutes] = useState(1);
  const [seconds, setSeconds] = useState(0);
  const [minutesPhone, setMinutesPhone] = useState(1);
  const [secondsPhone, setSecondsPhone] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalOpenPhone, setIsModalOpenPhone] = useState(false);

  const resolveAndSetPincode = useCallback(
    async (baseKey, pincodeNumber) => {
      const pin = String(pincodeNumber ?? "").trim();
      if (pin.length < 4) {
        if (baseKey === "billing") {
          form.setFieldsValue({ billing_pincode: null });
        } else {
          form.setFieldsValue({ [`${baseKey}_pincode`]: null });
        }
        return;
      }
      try {
        const res = await getRequest(`pincode/?search_pincode_number=${pin}`);
        const list = getPincodeListFromResponse(res);
        const row =
          list.find((r) => String(r.pincode_number ?? r.pincode) === pin) ??
          list[0];
        if (row) setAddressPincodeFromRow(form, baseKey, row);
      } catch {
        /* ignore */
      }
    },
    [form],
  );

  const applyPincodeToRegistered = useCallback(
    async (pincodeRaw) => {
      const pin = String(pincodeRaw ?? "").trim();
      if (pin.length < 4) return;
      form.setFieldsValue({
        registered_business_address_pincode_number: pin,
      });
      await resolveAndSetPincode("registered_business_address", pin);
    },
    [form, resolveAndSetPincode],
  );

  const handleGstNumberVerify = useCallback(async () => {
    const gst = String(form.getFieldValue("gst_no") ?? "").trim();
    if (!gst) {
      ErrorNotificationMsg(t("place_gst_no") || "Enter GST number");
      return;
    }
    setVerifyGstButtonView(true);
    try {
      setBtnLoadingVerifyGst(true);
      const apiResponse = await getRequest(`doc-verification/gst/?gst_no=${gst}`);
      const resData = apiResponse?.data;
      if (resData?.success === true) {
        form.setFieldsValue({ gst_no_verified: true });
        SuccessNotificationMsg("GST verified successfully");
        setVerificationStatus("success");
        setVerifyGstButtonView(false);
        const d = resData?.data;
        if (d) {
          const {
            legal_name,
            door_name,
            street,
            pincode,
          } = d;
          form.setFieldsValue({
            vendor_registered_name: legal_name ?? form.getFieldValue("vendor_registered_name"),
            registered_business_address_building:
              door_name ?? form.getFieldValue("registered_business_address_building"),
            registered_business_address_area:
              street ?? form.getFieldValue("registered_business_address_area"),
            registered_business_address_pincode_number:
              pincode ??
              form.getFieldValue("registered_business_address_pincode_number"),
          });
          if (pincode) await applyPincodeToRegistered(pincode);
        }
      } else {
        form.setFieldsValue({ gst_no_verified: false });
        setVerificationStatus(null);
        setVerifyGstButtonView(true);
        const rawMsg =
          resData?.message ??
          resData?.detail ??
          resData?.error ??
          "GST verification failed.";
        ErrorNotificationMsg(
          typeof rawMsg === "string" ? rawMsg : JSON.stringify(rawMsg),
        );
      }
    } catch (error) {
      form.setFieldsValue({ gst_no_verified: false });
      const rawMsg =
        error?.response?.data?.message ??
        error?.response?.data?.detail ??
        error?.message;
      ErrorNotificationMsg(
        typeof rawMsg === "string"
          ? rawMsg
          : t("Error in verifying GST number") || "Error verifying GST number",
      );
      setVerifyGstButtonView(true);
      setVerificationStatus(null);
    } finally {
      setBtnLoadingVerifyGst(false);
    }
  }, [applyPincodeToRegistered, form, t]);

  const handleAdhaarNumberVerify = useCallback(async () => {
    const adhaarNumber = String(form.getFieldValue("udyam_aadhaar_no") ?? "").trim();
    if (!adhaarNumber) {
      ErrorNotificationMsg(t("Enter udyam number") || "Enter Udyam number");
      return;
    }
    try {
      setBtnLoadingAdhaar(true);
      const response = await getRequest(
        `doc-verification/udhyam/?udhyam_no=${adhaarNumber}`,
      );
      if (response?.data?.success === true) {
        form.setFieldsValue({ udyam_aadhaar_no_verified: true });
        SuccessNotificationMsg("Udyam Number verified successfully");
        setVerificationResultAdhaar("success");
        setVerifyAdhaarButtonView(false);
      } else {
        form.setFieldsValue({ udyam_aadhaar_no_verified: false });
        setVerifyAdhaarButtonView(true);
        const resData = response?.data;
        const rawMsg =
          resData?.message ??
          resData?.detail ??
          resData?.error ??
          "Udyam verification failed.";
        ErrorNotificationMsg(
          typeof rawMsg === "string" ? rawMsg : JSON.stringify(rawMsg),
        );
      }
    } catch (error) {
      form.setFieldsValue({ udyam_aadhaar_no_verified: false });
      const rawMsg =
        error?.response?.data?.message ??
        error?.response?.data?.detail ??
        error?.message;
      ErrorNotificationMsg(
        typeof rawMsg === "string"
          ? rawMsg
          : t("Error in verifying number") || "Error verifying Udyam number",
      );
      setVerifyAdhaarButtonView(true);
    } finally {
      setBtnLoadingAdhaar(false);
    }
  }, [form, t]);

  const showModal = () => setIsModalOpen(true);
  const handleOk = () => setIsModalOpen(false);
  const handleCancel = () => {
    setIsModalOpen(false);
    setOTP(null);
    setSeconds(30);
    setMinutes(1);
    setBtnLoadingEmail(false);
  };

  const showModalPhone = () => setIsModalOpenPhone(true);
  const handleOkPhone = () => setIsModalOpenPhone(false);
  const handleCancelPhone = () => {
    setIsModalOpenPhone(false);
    setOTPPhone(null);
    setSecondsPhone(30);
    setMinutesPhone(1);
    setBtnLoadingPhone(false);
  };

  const handleSendOtpEmail = useCallback(async () => {
    try {
      setBtnLoadingEmail(true);
      const email = String(form.getFieldValue("email") ?? "").trim();
      setOtpContactState((prev) => ({ ...prev, email }));
      const payload = { email: email || "" };
      const response = await postRequest(`verified-otp/email-verify/`, payload);
      if (response?.data?.success) {
        const errorMessage = response?.data?.message || "";
        const emailVerificationRegex = /This [^ ]+ already Verify/;
        if (emailVerificationRegex.test(errorMessage)) {
          setVerifyEmailButtonView(false);
          setOtpContactState((prev) => ({ ...prev, email_verified: true }));
          form.setFieldsValue({ email_verified: true });
          setVerificationResultEmail("success");
        } else {
          setOtpContactState((prev) => ({ ...prev, email_verified: false }));
          form.setFieldsValue({ email_verified: false });
          showModal();
          setVerifyEmailButtonView(true);
        }
        SuccessNotificationMsg(response.data.message);
      } else {
        handleCancel();
        setVerificationResultEmail("");
      }
    } catch (error) {
      handleCancel();
      setVerifyEmailButtonView(true);
      setVerificationResultEmail("");
    } finally {
      setBtnLoadingEmail(false);
    }
  }, [form]);

  const handleEmailVerify = useCallback(async () => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const email = String(form.getFieldValue("email") ?? "").trim();
    if (emailPattern.test(email)) {
      await handleSendOtpEmail();
    } else {
      setVerifyEmailButtonView(true);
      ErrorNotificationMsg("Enter valid email");
    }
  }, [form, handleSendOtpEmail]);

  const handleSendOtpPhone = useCallback(async () => {
    try {
      setBtnLoadingPhone(true);
      const phone = form.getFieldValue("phone");
      setOtpContactState((prev) => ({ ...prev, phone: phone || "" }));
      const payload = { phone_number: phone || "" };
      const response = await postRequest(
        `verified-otp/phone-number-verify/`,
        payload,
      );
      if (response?.data?.success) {
        const errorMessage = response?.data?.message || "";
        const alreadyVerifiedRegex = /This [^ ]+ already Verify/;
        if (alreadyVerifiedRegex.test(errorMessage)) {
          setVerifyPhoneButtonView(false);
          setOtpContactState((prev) => ({ ...prev, phone_verified: true }));
          form.setFieldsValue({ phone_verified: true });
          setVerificationResultContact("success");
        } else {
          setOtpContactState((prev) => ({ ...prev, phone_verified: false }));
          form.setFieldsValue({ phone_verified: false });
          showModalPhone();
          setVerifyPhoneButtonView(true);
        }
        SuccessNotificationMsg(response.data.message);
      } else {
        setVerificationResultContact("");
        handleCancelPhone();
        setVerifyPhoneButtonView(true);
      }
    } catch (error) {
      setVerifyPhoneButtonView(true);
      handleCancelPhone();
      setVerificationResultContact("");
    } finally {
      setBtnLoadingPhone(false);
    }
  }, [form]);

  const handleContactNumberVerify = useCallback(async () => {
    const contactNumber = form.getFieldValue("phone");
    const phoneNumberPattern = /^\d{12}$/;
    if (contactNumber && phoneNumberPattern.test(String(contactNumber))) {
      await handleSendOtpPhone();
    } else {
      ErrorNotificationMsg("Enter valid mobile number");
    }
  }, [form, handleSendOtpPhone]);

  const sameAsRegistered = Form.useWatch(
    "same_as_registered_business_address",
    form,
  );
  const gstNoWatch = Form.useWatch("gst_no", form);
  const udyamWatch = Form.useWatch("udyam_aadhaar_no", form);
  const emailWatch = Form.useWatch("email", form);
  const phoneWatch = Form.useWatch("phone", form);

  const fetchPincodeList = useCallback(async (q, setter) => {
    const v = String(q || "").trim();
    if (v.length < 4) {
      setter([]);
      return;
    }
    try {
      const res = await getRequest(`pincode/?search_pincode_number=${v}`);
      const list = res?.data?.results?.data ?? res?.data?.data ?? [];
      setter(Array.isArray(list) ? list : []);
    } catch {
      setter([]);
    }
  }, []);

  const debouncedRegPin = useRef(
    debounce((q) => fetchPincodeList(q, setRegisteredPinOptions), 400),
  ).current;
  const debouncedTradPin = useRef(
    debounce((q) => fetchPincodeList(q, setTradingPinOptions), 400),
  ).current;
  const debouncedBillPin = useRef(
    debounce((q) => fetchPincodeList(q, setBillingPinOptions), 400),
  ).current;
  const debouncedResolveRegPin = useRef(
    debounce((v) => resolveAndSetPincode("registered_business_address", v), 400),
  ).current;
  const debouncedResolveTradPin = useRef(
    debounce((v) => resolveAndSetPincode("trading_address", v), 400),
  ).current;
  const debouncedResolveBillPin = useRef(
    debounce((v) => resolveAndSetPincode("billing", v), 400),
  ).current;

  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    const load = async () => {
      setDetailLoading(true);
      try {
        const res = await getRequest(`general-procurement-vendor/${editId}/`);
        const raw =
          res?.data?.results?.data ??
          res?.data?.data ??
          res?.data?.results ??
          res?.data;
        const v = Array.isArray(raw) ? raw[0] : raw;
        if (!cancelled && v) {
          const mapped = mapVendorApiToForm(v);
          form.setFieldsValue(mapped);
          setBillingPhoneInputKey((k) => k + 1);
          setOtpContactState({
            email: mapped.email ?? "",
            phone: mapped.phone ?? "",
            email_verified: !!mapped.email_verified,
            phone_verified: !!mapped.phone_verified,
          });
          setVerifyGstButtonView(!v.gst_no_verified);
          setVerificationStatus(v.gst_no_verified ? "success" : null);
          setVerifyAdhaarButtonView(!v.udyam_aadhaar_no_verified);
          setVerificationResultAdhaar(
            v.udyam_aadhaar_no_verified ? "success" : null,
          );
          setVerifyEmailButtonView(!v.email_verified);
          setVerificationResultEmail(v.email_verified ? "success" : null);
          setVerifyPhoneButtonView(!v.phone_verified);
          setVerificationResultContact(v.phone_verified ? "success" : null);
        }
      } catch (e) {
        if (!cancelled) {
          ShowErrorMsg(e);
          ErrorNotificationMsg("Could not load vendor for edit.");
        }
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [editId, isEdit, form]);

  useEffect(() => {
    if (!sameAsRegistered) return;
    const b = form.getFieldValue("registered_business_address_building");
    const a = form.getFieldValue("registered_business_address_area");
    const l = form.getFieldValue("registered_business_address_landmark");
    const p = form.getFieldValue("registered_business_address_pincode");
    const pn = form.getFieldValue("registered_business_address_pincode_number");
    const c = form.getFieldValue("registered_business_address_city");
    const s = form.getFieldValue("registered_business_address_state");
    form.setFieldsValue({
      trading_address_building: b,
      trading_address_area: a,
      trading_address_landmark: l,
      trading_address_pincode: p,
      trading_address_pincode_number: pn,
      trading_address_city: c,
      trading_address_state: s,
    });
  }, [sameAsRegistered, form]);

  const onSelectRegisteredPin = (_, option) => {
    if (option?.row) {
      setAddressPincodeFromRow(form, "registered_business_address", option.row);
    }
  };

  const onSelectTradingPin = (_, option) => {
    if (option?.row) {
      setAddressPincodeFromRow(form, "trading_address", option.row);
    }
  };

  const onSelectBillingPin = (_, option) => {
    if (option?.row) {
      setAddressPincodeFromRow(form, "billing", option.row);
    }
  };

  const pinOptionsToSelect = (list) =>
    (list || []).map((row) => ({
      value: String(row.pincode_number ?? row.pincode ?? ""),
      label: String(row.pincode_number ?? row.pincode ?? ""),
      row,
    }));

  const gstMainWatch = Form.useWatch("gst_no", form);
  const gstBillWatch = Form.useWatch("billing_gst_for_billing", form);
  const paymentSecurityTypeWatch = Form.useWatch("payment_security_type", form);
  const showBankGuaranteeFields =
    paymentSecurityTypeWatch === "Secure with Bank Garrentee";
  const showLCFields = paymentSecurityTypeWatch === "Secure with LC";

  useEffect(() => {
    if (!paymentSecurityTypeWatch) return;
    if (paymentSecurityTypeWatch !== "Secure with Bank Garrentee") {
      form.setFieldsValue({
        bank_garrentee_value: "",
        bank_guarantee_supply_limit_value: "",
        bank_garrentee_open_date: null,
        bank_garrentee_validity_complation_date: null,
      });
    }
    if (paymentSecurityTypeWatch !== "Secure with LC") {
      form.setFieldsValue({
        lc_value: "",
        lc_supply_limit_value: "",
        lc_open_date: null,
        lc_validity_complation_date: null,
      });
    }
  }, [paymentSecurityTypeWatch, form]);
  const gstForBankOptions = useMemo(() => {
    const arr = [];
    if (gstMainWatch) arr.push(String(gstMainWatch));
    if (gstBillWatch && String(gstBillWatch) !== String(gstMainWatch)) {
      arr.push(String(gstBillWatch));
    }
    return [...new Set(arr.filter(Boolean))];
  }, [gstMainWatch, gstBillWatch]);

  const handleAddDocumentRow = () => {
    const name = String(docNameInput || "").trim();
    if (!name) {
      message.warning("Enter document name.");
      return;
    }
    if (!docFile) {
      message.warning("Choose a file to upload (max 2 MB, .jpg, .png, .pdf).");
      return;
    }
    const allowed = ["image/jpeg", "image/png", "application/pdf"];
    if (!allowed.includes(docFile.type)) {
      message.error("Only JPG, PNG, or PDF files are allowed.");
      return;
    }
    if (docFile.size > 2097152) {
      message.error("Maximum file size is 2 MB.");
      return;
    }
    setDocumentRows((prev) => [
      ...prev,
      {
        key: `${Date.now()}-${prev.length}`,
        document_name: name,
        file_name: docFile.name,
        file: docFile,
      },
    ]);
    setDocNameInput("");
    setDocFile(null);
    message.success("Document added to the list. Submit the form to save the vendor.");
  };

  const ensurePincodeIdsBeforeSubmit = useCallback(async () => {
    const v = form.getFieldsValue();
    if (
      !v.registered_business_address_pincode &&
      v.registered_business_address_pincode_number
    ) {
      await resolveAndSetPincode(
        "registered_business_address",
        v.registered_business_address_pincode_number,
      );
    }
    if (!v.trading_address_pincode && v.trading_address_pincode_number) {
      await resolveAndSetPincode(
        "trading_address",
        v.trading_address_pincode_number,
      );
    }
    if (!v.billing_pincode && v.billing_pincode_number) {
      await resolveAndSetPincode("billing", v.billing_pincode_number);
    }
    return form.getFieldsValue();
  }, [form, resolveAndSetPincode]);

  const handleFinish = async (values) => {
    try {
      const CompanyId = authUserData()?.company ?? null;
      const resolvedValues = await ensurePincodeIdsBeforeSubmit();
      const payload = buildVendorFormDataPayload(
        { ...values, ...resolvedValues },
        { CompanyId },
      );
      console.log(payload)
      const formData = new FormData();
      formData.append("form_data", JSON.stringify(payload));
      documentRows.forEach((d, idx) => {
        if (d.file instanceof File) {
          formData.append(`documents[${idx}][file]`, d.file);
          formData.append(`documents[${idx}][document_name]`, d.document_name);
        }
      });

      const customerHeaders = {
        headers: { "Content-Type": "multipart/form-data" },
      };

      setSubmitting(true);
      if (isEdit) {
        await patchRequest(
          `general-procurement-vendor/${editId}/`,
          formData,
          customerHeaders,
        );
        SuccessNotificationMsg("Vendor updated successfully.");
      } else {
        await postRequest("general-procurement-vendor/", formData, customerHeaders);
        SuccessNotificationMsg("Vendor created successfully.");
      }
      navigate("/general-vendor-list");
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.detail ||
        e?.message ||
        (isEdit ? "Failed to update vendor." : "Failed to create vendor.");
      ErrorNotificationMsg(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setSubmitting(false);
    }
  };

  const docColumns = [
    { title: "Title", dataIndex: "document_name", key: "document_name" },
    { title: "Document", dataIndex: "file_name", key: "file_name" },
    {
      title: "Action",
      key: "act",
      width: 80,
      render: (_, row) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() =>
            setDocumentRows((prev) => prev.filter((r) => r.key !== row.key))
          }
        />
      ),
    },
  ];

  return (
    <>
      <div className="ListingTopBar">
        <div className="pagenamewrap">
          <div className="pagename">
            <h3>{isEdit ? "Edit Vendor" : "Add Vendor"}</h3>
            <Breadcrumb className="Breadcrumb-Sec">
              <Breadcrumb.Item>
                <Link to="/dashboard">{t("home")}</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                <Link to="/general-vendor-list">General Vendors — History</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>{isEdit ? "Edit Vendor" : "Add Vendor"}</Breadcrumb.Item>
            </Breadcrumb>
          </div>
        </div>
      </div>

      <div className="stepscontentwrap">
        <div className="stepsContent">
          <Spin spinning={detailLoading}>
            <Form
              form={form}
              layout="vertical"
              className="formwrapper"
              style={{ paddingRight: "5px" }}
              autoComplete="off"
              onFinish={handleFinish}
              onValuesChange={(changed) => {
                if (Object.prototype.hasOwnProperty.call(changed, "gst_no")) {
                  form.setFieldsValue({ gst_no_verified: false });
                  setVerifyGstButtonView(true);
                  setVerificationStatus(null);
                }
                if (
                  Object.prototype.hasOwnProperty.call(changed, "udyam_aadhaar_no")
                ) {
                  form.setFieldsValue({ udyam_aadhaar_no_verified: false });
                  setVerifyAdhaarButtonView(true);
                  setVerificationResultAdhaar(null);
                }
                if (Object.prototype.hasOwnProperty.call(changed, "email")) {
                  form.setFieldsValue({ email_verified: false });
                  setVerifyEmailButtonView(true);
                  setVerificationResultEmail("");
                }
                if (Object.prototype.hasOwnProperty.call(changed, "phone")) {
                  form.setFieldsValue({ phone_verified: false });
                  setVerifyPhoneButtonView(true);
                  setVerificationResultContact("");
                }
              }}
              initialValues={{
                same_as_registered_business_address: false,
                email_verified: false,
                phone_verified: false,
                gst_no_verified: false,
                udyam_aadhaar_no_verified: false,
                payment_days_to_pay: "7 days",
                payment_when_to_pay: "Within",
                payment_when_to_calculate: "From the date of invoice",
                payment_advance: 0,
                payment_security_type: "Open Credit",
              }}
            >
              <Form.Item name="email_verified" valuePropName="checked" hidden>
                <Checkbox />
              </Form.Item>
              <Form.Item name="phone_verified" valuePropName="checked" hidden>
                <Checkbox />
              </Form.Item>
              <Form.Item name="gst_no_verified" valuePropName="checked" hidden>
                <Checkbox />
              </Form.Item>
              <Form.Item
                name="udyam_aadhaar_no_verified"
                valuePropName="checked"
                hidden
              >
                <Checkbox />
              </Form.Item>
              <Collapse defaultActiveKey={["1", "2", "3", "4", "5"]}>
                <Panel header="Business Info" key="1">
                  <Row gutter={16}>
                    <Col xs={24} sm={24} md={12} xl={8} xxl={6} className="TextFeild gutter-row">
                      <div className="verifywrap">
                        <Form.Item
                          name="gst_no"
                          label={t("place_gst_no5")}
                          rules={[
                            {
                              validator: (_, value) => {
                                const v = String(value || "").trim();
                                if (!v) return Promise.resolve();
                                const re =
                                  /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
                                return re.test(v)
                                  ? Promise.resolve()
                                  : Promise.reject(new Error(t("val_gst_no")));
                              },
                            },
                          ]}
                        >
                          <Input prefix={<IdcardOutlined />} placeholder={t("place_gst_no")} />
                        </Form.Item>
                        {verifyGstButtonView ? (
                          <ButtonField
                            type="primary"
                            label={t("Verify")}
                            className="SaveGradiantBtn verify"
                            htmlType="button"
                            loading={btnLoadingVerifyGst}
                            onClick={handleGstNumberVerify}
                            disabled={!String(gstNoWatch ?? "").trim()}
                          />
                        ) : (
                          verificationStatus === "success" && (
                            <img
                              src={verifybtn}
                              alt=""
                              style={{ height: "36px", marginTop: "29px" }}
                            />
                          )
                        )}
                      </div>
                    </Col>
                    <Col xs={24} sm={24} md={12} xl={8} xxl={6} className="TextFeild gutter-row">
                      <div className="verifywrap">
                        <Form.Item name="udyam_aadhaar_no" label={t("Udyam Number")}>
                          <Input prefix={<IdcardOutlined />} placeholder={t("Enter udyam number")} />
                        </Form.Item>
                        {verifyAdhaarButtonView ? (
                          <ButtonField
                            type="primary"
                            label={t("Verify")}
                            className="SaveGradiantBtn verify"
                            htmlType="button"
                            loading={btnLoadingAdhaar}
                            onClick={handleAdhaarNumberVerify}
                            disabled={!String(udyamWatch ?? "").trim()}
                          />
                        ) : (
                          verificationResultAdhaar === "success" && (
                            <img
                              src={verifybtn}
                              alt=""
                              style={{ height: "36px", marginTop: "29px" }}
                            />
                          )
                        )}
                      </div>
                    </Col>
                    <Col xs={24} sm={24} md={12} xl={8} xxl={6}>
                      <Form.Item
                        name="vendor_registered_name"
                        label={t("label_vendor_company_name")}
                        rules={[{ required: true, message: t("val_vendor_company_name") }]}
                      >
                        <Input prefix={<Vector />} maxLength={40} placeholder={t("pl_vendor_company_name")} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12} xl={8} xxl={6}>
                      <Form.Item
                        name="vendor_trade_name"
                        label={t("Vendor Trade Name")}
                        rules={[{ required: true, message: t("Please enter vendor trade name") }]}
                      >
                        <Input prefix={<AddressLine />} maxLength={40} placeholder="Enter vendor trade name" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12} xl={8} xxl={6}>
                      <Form.Item
                        name="vendor_code_by_company"
                        label={t("Vendor Code as per Company ERP System")}
                      >
                        <Input prefix={<FileAlt />} placeholder={t("pl_enter_vendor_code")} />
                      </Form.Item>
                    </Col>
                  </Row>

                  <div className="vendorsubtitle">Contact Information</div>
                  <Row gutter={16}>
                    <Col xs={24} sm={24} md={12} xl={8} xxl={6}>
                      <Form.Item
                        name="person_name"
                        label={t("label_person_name")}
                        rules={[{ required: true, message: t("val_person_name") }]}
                      >
                        <Input prefix={<Name />} maxLength={30} placeholder={t("pl_person_name")} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12} xl={8} xxl={6}>
                      <Form.Item
                        name="designation"
                        label={t("label_designation")}
                        rules={[{ required: true, message: t("val_designation") }]}
                      >
                        <Input prefix={<AddressLine />} maxLength={30} placeholder={t("pl_designation")} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12} xl={8} xxl={6}>
                      <div className="verifywrap">
                        <Form.Item
                          name="email"
                          label={t("label_email")}
                          rules={[{ required: true, type: "email", message: t("val_email") }]}
                        >
                          <Input prefix={<Email />} placeholder={t("pl_email_2")} />
                        </Form.Item>
                        {verifyEmailButtonView ? (
                          <ButtonField
                            type="primary"
                            label={t("Verify")}
                            className="SaveGradiantBtn verify"
                            htmlType="button"
                            loading={btnLoadingEmail}
                            onClick={handleEmailVerify}
                            disabled={!String(emailWatch ?? "").trim()}
                          />
                        ) : (
                          verificationResultEmail === "success" && (
                            <img
                              src={verifybtn}
                              alt=""
                              style={{ height: "36px", marginTop: "29px" }}
                            />
                          )
                        )}
                      </div>
                    </Col>
                    <Col xs={24} sm={24} md={12} xl={8} xxl={6}>
                      <div className="verifywrap">
                        <Form.Item
                          name="phone"
                          label={t("label_mobileNumber")}
                          rules={[{ required: true, message: t("val_mobileNumber") }]}
                          getValueFromEvent={(v) => v}
                        >
                          <PhoneInput country="in" inputClass="PhoneSection" />
                        </Form.Item>
                        {verifyPhoneButtonView ? (
                          <ButtonField
                            type="primary"
                            label={t("Verify")}
                            className="SaveGradiantBtn verify"
                            htmlType="button"
                            loading={btnLoadingPhone}
                            onClick={handleContactNumberVerify}
                            disabled={!phoneWatch}
                          />
                        ) : (
                          verificationResultContact === "success" && (
                            <img
                              src={verifybtn}
                              alt=""
                              style={{ height: "36px", marginTop: "29px" }}
                            />
                          )
                        )}
                      </div>
                    </Col>
                  </Row>

                  <Row gutter={32}>
                    <Col xs={24} xl={12} className="bdr">
                      <div className="colwrap">
                        <div className="colsubtitle">Registered Business Address</div>
                        <Row gutter={16}>
                          <Col xs={24} md={12}>
                            <Form.Item
                              name="registered_business_address_building"
                              label={t("Shop No., Building, Apartment")}
                              rules={[{ required: true, message: t("Please enter shop no., building, company, apartment") }]}
                            >
                              <Input maxLength={50} placeholder={t("Enter shop no., building, apartment")} />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={12}>
                            <Form.Item
                              name="registered_business_address_area"
                              label={t("Area, Street")}
                              rules={[{ required: true, message: t("Please enter Area, Street") }]}
                            >
                              <Input maxLength={50} placeholder={t("Enter area, street")} />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={12}>
                            <Form.Item
                              name="registered_business_address_landmark"
                              label={t("Landmark")}
                              rules={[{ required: true, message: t("Please enter landmark") }]}
                            >
                              <Input maxLength={50} placeholder={t("Enter landmark")} />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={12}>
                            <Form.Item name="registered_business_address_pincode" hidden>
                              <Input type="hidden" />
                            </Form.Item>
                            <Form.Item
                              name="registered_business_address_pincode_number"
                              label={t("pl_pincode")}
                              rules={[{ required: true, message: t("val_postal_zip_code") }]}
                            >
                              <AutoComplete
                                className="autoComplete"
                                options={pinOptionsToSelect(registeredPinOptions)}
                                onSearch={(v) => debouncedRegPin(v)}
                                onSelect={onSelectRegisteredPin}
                                onChange={(v) => {
                                  form.setFieldsValue({
                                    registered_business_address_pincode_number: v,
                                  });
                                  debouncedResolveRegPin(v);
                                }}
                                placeholder={t("pl_pincodeselect")}
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={12}>
                            <Form.Item
                              name="registered_business_address_city"
                              label={t("pl_city")}
                              rules={[{ required: true, message: t("val_city") }]}
                            >
                              <Input placeholder={t("place_city")} disabled />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={12}>
                            <Form.Item
                              name="registered_business_address_state"
                              label={t("pl_state_province")}
                              rules={[{ required: true, message: t("val_state_province") }]}
                            >
                              <Input placeholder={t("place_state")} disabled />
                            </Form.Item>
                          </Col>
                        </Row>
                      </div>
                    </Col>
                    <Col xs={24} xl={12}>
                      <div className="colwrap">
                        <div className="colsubtitle">
                          Trading Address
                          <Tooltip title="Same as registered business address">
                            <Form.Item
                              name="same_as_registered_business_address"
                              valuePropName="checked"
                              noStyle
                            >
                              <Checkbox style={{ marginLeft: 10 }}>Same as my business address</Checkbox>
                            </Form.Item>
                          </Tooltip>
                        </div>
                        <Row gutter={16}>
                          <Col xs={24} md={12} className="TextFeild gutter-row">
                            <Form.Item
                              name="trading_address_building"
                              label={t("Shop No., Building, Apartment")}
                              rules={[{ required: true, message: t("Please enter shop no., building, apartment") }]}
                            >
                              <Input
                                maxLength={50}
                                disabled={sameAsRegistered}
                                placeholder={t("Enter shop no., building, apartment")}
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={12} className="TextFeild gutter-row">
                            <Form.Item
                              name="trading_address_area"
                              label={t("Area, Street")}
                              rules={[{ required: true, message: t("Please enter Area, Street") }]}
                            >
                              <Input
                                maxLength={50}
                                disabled={sameAsRegistered}
                                placeholder={t("Enter area, street")}
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={12} className="TextFeild gutter-row">
                            <Form.Item
                              name="trading_address_landmark"
                              label={t("Landmark")}
                              rules={[{ required: true, message: t("Please enter landmark") }]}
                            >
                              <Input
                                maxLength={50}
                                disabled={sameAsRegistered}
                                placeholder={t("Enter landmark")}
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={12} className="TextFeild gutter-row">
                            <Form.Item name="trading_address_pincode" hidden>
                              <Input type="hidden" />
                            </Form.Item>
                            <Form.Item
                              name="trading_address_pincode_number"
                              label={t("pl_pincode")}
                              rules={[{ required: true, message: t("val_postal_zip_code") }]}
                            >
                              <AutoComplete
                                className="autoComplete"
                                disabled={sameAsRegistered}
                                options={pinOptionsToSelect(tradingPinOptions)}
                                onSearch={(v) => !sameAsRegistered && debouncedTradPin(v)}
                                onSelect={onSelectTradingPin}
                                onChange={(v) => {
                                  if (sameAsRegistered) return;
                                  form.setFieldsValue({
                                    trading_address_pincode_number: v,
                                  });
                                  debouncedResolveTradPin(v);
                                }}
                                placeholder={t("pl_pincodeselect")}
                                allowClear
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={12} className="TextFeild gutter-row">
                            <Form.Item
                              name="trading_address_city"
                              label={t("pl_city")}
                              rules={[{ required: true, message: t("val_city") }]}
                            >
                              <Input placeholder={t("place_city")} disabled />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={12} className="TextFeild gutter-row">
                            <Form.Item
                              name="trading_address_state"
                              label={t("pl_state_province")}
                              rules={[{ required: true, message: t("val_state_province") }]}
                            >
                              <Input placeholder={t("place_state")} disabled />
                            </Form.Item>
                          </Col>
                        </Row>
                      </div>
                    </Col>
                  </Row>
                </Panel>

                <Panel header="Vendor Billing Details" key="2">
                  <Row gutter={16} className="customcol">
                    <Col
                      xs={24}
                      sm={12}
                      md={6}
                      className="TextFeild gutter-row"
                    >
                      <Form.Item
                        name="billing_gst_for_billing"
                        label="Vendor GST For Billing"
                        rules={[{ required: true, message: "Enter GST for billing" }]}
                      >
                        <Input placeholder={t("22AAAAA0000A1Z5") || "22AAAAA0000A1Z5"} />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={12}
                      md={6}
                      className="TextFeild gutter-row"
                    >
                      <Form.Item
                        name="billing_address_line_1"
                        label={t("Shop No., Building, Apartment")}
                        rules={[{ required: true, message: t("Please Enter Address") }]}
                      >
                        <Input
                          maxLength={50}
                          placeholder={t("16, Block B, Ganesh Park") || "Enter shop no., building, apartment"}
                        />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={12}
                      md={6}
                      className="TextFeild gutter-row"
                    >
                      <Form.Item
                        name="billing_address_line_2"
                        label={t("Area, Street")}
                        rules={[{ required: true, message: t("Please Enter Area,Street") }]}
                      >
                        <Input
                          maxLength={50}
                          placeholder={t("Gota") || t("Enter area, street") || "Enter area, street"}
                        />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={12}
                      md={6}
                      className="TextFeild gutter-row"
                    >
                      <Form.Item
                        name="billing_person_name"
                        label={t("pl_personName") || "Person Name"}
                        rules={[{ required: true, message: t("Please Enter Person Name") }]}
                      >
                        <Input
                          maxLength={30}
                          placeholder={t("pl_personName") || t("pl_person_name") || "Enter person name"}
                        />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={12}
                      md={6}
                      className="TextFeild gutter-row"
                    >
                      <Form.Item
                        name="billing_landmark"
                        label={t("Landmark")}
                        rules={[{ required: true, message: t("Please Enter Landmark") }]}
                      >
                        <Input
                          maxLength={50}
                          placeholder={t("Enter landmark") || "Enter landmark"}
                        />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={12}
                      md={6}
                      className="TextFeild gutter-row"
                    >
                      <Form.Item
                        name="billing_email"
                        label={t("lbb_email") || t("label_email")}
                        rules={[{ required: true, type: "email", message: t("val_email") }]}
                      >
                        <Input placeholder={t("pl_email") || t("pl_email_2")} />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={12}
                      md={6}
                      className="TextFeild gutter-row"
                    >
                      <Form.Item
                        name="billing_phone"
                        label={t("pl_ContactNo") || t("label_mobileNumber")}
                        rules={[{ required: true, message: t("val_mobileNumber") }]}
                        getValueFromEvent={(v) => v}
                        getValueProps={(value) => ({
                          value: value ?? "",
                        })}
                      >
                        <PhoneInput
                          key={`billing-phone-${billingPhoneInputKey}`}
                          country="in"
                          inputClass="PhoneSection"
                          containerClass="phonefield"
                          placeholder={t("pl_mobileNumber") || "Enter mobile number"}
                        />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={12}
                      md={6}
                      className="TextFeild gutter-row"
                    >
                      <Form.Item name="billing_pincode" hidden>
                        <Input type="hidden" />
                      </Form.Item>
                      <Form.Item
                        name="billing_pincode_number"
                        label={t("pl_pincode")}
                        rules={[{ required: true, message: t("val_postal_zip_code") }]}
                      >
                        <AutoComplete
                          className="autoComplete"
                          options={pinOptionsToSelect(billingPinOptions)}
                          onSearch={(v) => debouncedBillPin(v)}
                          onSelect={onSelectBillingPin}
                          onChange={(v) => {
                            form.setFieldsValue({ billing_pincode_number: v });
                            debouncedResolveBillPin(v);
                          }}
                          placeholder={t("pl_pincodeselect")}
                          allowClear
                        />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={12}
                      md={6}
                      className="TextFeild gutter-row"
                    >
                      <Form.Item
                        name="billing_city"
                        label={t("pl_city")}
                        rules={[{ required: true, message: t("val_city") }]}
                      >
                        <Input placeholder={t("place_city")} disabled />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={12}
                      md={6}
                      className="TextFeild gutter-row"
                    >
                      <Form.Item
                        name="billing_state"
                        label={t("pl_state_province")}
                        rules={[{ required: true, message: t("val_state_province") }]}
                      >
                        <Input placeholder={t("place_state")} disabled />
                      </Form.Item>
                    </Col>
                  </Row>
                </Panel>

                <Panel header="Bank Details" key="3">
                  <Row gutter={16} className="customcol bank_details_sec">
                    <Col
                      xs={24}
                      sm={12}
                      md={6}
                      className="TextFeild gutter-row"
                    >
                      <Form.Item
                        name="bank_account_holder_name"
                        label={t("label_account_holder_name")}
                        rules={[{ required: true, message: t("Please Enter Valid Name") }]}
                      >
                        <Input
                          className="cust-input"
                          maxLength={30}
                          placeholder={t("pl_account_holder_name")}
                        />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={12}
                      md={6}
                      className="TextFeild gutter-row"
                    >
                      <Form.Item
                        name="bank_account_number"
                        label={t("label_account_number")}
                        rules={[
                          {
                            required: true,
                            pattern: /^[0-9]{9,18}$/,
                            message: "Please enter valid account number",
                          },
                        ]}
                      >
                        <Input
                          className="cust-input"
                          placeholder={t("pl_account_number")}
                        />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={12}
                      md={6}
                      className="TextFeild gutter-row"
                    >
                      <Form.Item
                        name="bank_name"
                        label={t("label_bank_name")}
                        rules={[{ required: true, message: t("Please Enter Valid Name") }]}
                      >
                        <Input
                          className="cust-input"
                          maxLength={50}
                          placeholder={t("pl_bank_name")}
                        />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={12}
                      md={6}
                      className="TextFeild gutter-row"
                    >
                      <Form.Item
                        name="bank_branch_name"
                        label={t("label_branch_name")}
                        rules={[{ required: true, message: t("Please Enter Valid Name") }]}
                      >
                        <Input
                          className="cust-input"
                          maxLength={50}
                          placeholder={t("pl_branch_name")}
                        />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={12}
                      md={6}
                      className="TextFeild gutter-row"
                    >
                      <Form.Item
                        name="bank_ifsc_code"
                        label={t("label_ifsc_code")}
                        rules={[{ required: true, message: t("Please Enter Valid IFSC") }]}
                      >
                        <Input
                          className="cust-input"
                          maxLength={11}
                          placeholder={t("pl_ifsc_code")}
                        />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={12}
                      md={6}
                      className="TextFeild gutter-row"
                    >
                      <Form.Item
                        name="bank_gstin"
                        label={t("GSTIN")}
                        rules={[{ required: true, message: t("Please Select GSTN") }]}
                      >
                        <Select
                          showSearch
                          allowClear
                          placeholder={t("Select GSTN")}
                          optionFilterProp="label"
                          options={gstForBankOptions.map((g) => ({
                            label: g,
                            value: g,
                          }))}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Panel>

                <Panel header="Payment Terms" key="4">
                  <Row gutter={[15]}>
                    <Col xs={24} sm={12} md={6} className="TextFeild gutter-row">
                      <Form.Item
                        name="payment_days_to_pay"
                        label="Days To Pay"
                        rules={[{ required: true, message: t("val_days_to_pay") }]}
                      >
                        <Select showSearch allowClear placeholder={t("pl_days_to_pay")}>
                          <Option value="7 days">7 days</Option>
                          <Option value="15 days">15 days</Option>
                          <Option value="21 days">21 days</Option>
                          <Option value="30 days">30 days</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={6} className="TextFeild gutter-row">
                      <Form.Item
                        name="payment_when_to_pay"
                        label="When To Pay"
                        rules={[{ required: true, message: t("val_when_to_pay") }]}
                      >
                        <Select showSearch allowClear placeholder={t("pl_when_to_pay")}>
                          <Option value="Within">Within</Option>
                          <Option value="NET">NET</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={6} className="TextFeild gutter-row">
                      <Form.Item
                        name="payment_when_to_calculate"
                        label="From When To Calculate"
                        rules={[{ required: true, message: t("val_when_to_calculate") }]}
                      >
                        <Select showSearch allowClear placeholder={t("pl_when_to_calculate")}>
                          <Option value="From the date of invoice">From the date of invoice</Option>
                          <Option value="From the date of reciept of material at site">
                            From the date of reciept of material at site
                          </Option>
                          <Option value="From the good reciept note(GRN)">From the good reciept note(GRN)</Option>
                          <Option value="Prior to dispach">Prior to dispach</Option>
                          <Option value="Others">Others</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={6} className="TextFeild gutter-row">
                      <Form.Item
                        name="payment_advance"
                        label="Advance in %"
                        rules={[
                          { required: true, message: t("val_advance") },
                          { pattern: /^(100|[1-9]?[0-9])$/, message: "Enter a number between 0 and 100" },
                        ]}
                      >
                        <Input type="number" min={0} max={100} prefix={<PercentageOutlined />} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={6} className="TextFeild gutter-row">
                      <Form.Item
                        name="payment_security_type"
                        label="Select payment security type"
                        rules={[{ required: true, message: t("val_payment_security_type") }]}
                      >
                        <Select showSearch allowClear placeholder={t("pl_payment_security_type")}>
                          <Option value="Open Credit">Open Credit</Option>
                          <Option value="Secure with Bank Garrentee">Secure with Bank Guarantee</Option>
                          <Option value="Secure with LC">Secure with LC</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  {showBankGuaranteeFields && (
                    <Row gutter={[15]}>
                      <Col xs={24} sm={12} md={6} className="TextFeild gutter-row">
                        <Form.Item
                          name="bank_garrentee_value"
                          label={t("pl_bank_garrentee_value") || "Bank Guarantee Value"}
                          rules={[
                            {
                              required: true,
                              message:
                                t("val_bank_garrentee_value") ||
                                "Please enter bank guarantee value",
                            },
                          ]}
                        >
                          <Input
                            type="number"
                            addonBefore="₹"
                            min={0}
                            placeholder={t("pl_bank_garrentee_value") || "Enter value"}
                            onKeyPress={(e) => {
                              if (e.key === "." || e.key === "e") {
                                e.preventDefault();
                              }
                            }}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={6} className="TextFeild gutter-row">
                        <Form.Item
                          name="bank_guarantee_supply_limit_value"
                          label={
                            t("pl_bank_guarantee_supply_limit_value") ||
                            "Supply Limit Value"
                          }
                          rules={[
                            {
                              required: true,
                              message:
                                t("val_bank_guarantee_supply_limit_value") ||
                                "Please enter supply limit value",
                            },
                          ]}
                        >
                          <Input
                            type="number"
                            addonBefore="₹"
                            min={0}
                            placeholder={
                              t("pl_bank_guarantee_supply_limit_value") ||
                              "Enter supply limit"
                            }
                            onKeyPress={(e) => {
                              if (e.key === "." || e.key === "e") {
                                e.preventDefault();
                              }
                            }}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={6} className="TextFeild gutter-row">
                        <Form.Item
                          name="bank_garrentee_open_date"
                          label={
                            t("pl_bank_garrentee_open_date") ||
                            "Bank Guarantee Open Date"
                          }
                          rules={[
                            {
                              required: true,
                              message:
                                t("val_postal_open_date") ||
                                "Please select open date",
                            },
                          ]}
                        >
                          <DatePicker style={{ width: "100%" }} format="DD-MM-YY" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={6} className="TextFeild gutter-row">
                        <Form.Item
                          name="bank_garrentee_validity_complation_date"
                          label={
                            t("pl_bank_garrentee_validity_complation_date") ||
                            "Validity Completion Date"
                          }
                          rules={[
                            {
                              required: true,
                              message:
                                t("val_postal_validity_complation") ||
                                "Please select validity completion date",
                            },
                          ]}
                        >
                          <DatePicker style={{ width: "100%" }} format="DD-MM-YY" />
                        </Form.Item>
                      </Col>
                    </Row>
                  )}

                  {showLCFields && (
                    <Row gutter={[15]}>
                      <Col xs={24} sm={12} md={6} className="TextFeild gutter-row">
                        <Form.Item
                          name="lc_value"
                          label={t("pl_lc_value") || "LC Value"}
                          rules={[
                            {
                              required: true,
                              message: t("val_lc_value") || "Please enter LC value",
                            },
                          ]}
                        >
                          <Input
                            type="number"
                            addonBefore="₹"
                            min={0}
                            placeholder={t("pl_lc_value") || "Enter LC value"}
                            onKeyPress={(e) => {
                              if (e.key === "." || e.key === "e") {
                                e.preventDefault();
                              }
                            }}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={6} className="TextFeild gutter-row">
                        <Form.Item
                          name="lc_supply_limit_value"
                          label={
                            t("pl_lc_supply_limit_value") || "LC Supply Limit Value"
                          }
                          rules={[
                            {
                              required: true,
                              message:
                                t("val_lc_supply_limit_value") ||
                                "Please enter LC supply limit",
                            },
                          ]}
                        >
                          <Input
                            type="number"
                            addonBefore="₹"
                            min={0}
                            placeholder={
                              t("pl_lc_supply_limit_value") || "Enter supply limit"
                            }
                            onKeyPress={(e) => {
                              if (e.key === "." || e.key === "e") {
                                e.preventDefault();
                              }
                            }}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={6} className="TextFeild gutter-row">
                        <Form.Item
                          name="lc_open_date"
                          label={t("pl_lc_open_date") || "LC Open Date"}
                          rules={[
                            {
                              required: true,
                              message:
                                t("val_postal_open_date") ||
                                "Please select LC open date",
                            },
                          ]}
                        >
                          <DatePicker style={{ width: "100%" }} format="DD-MM-YY" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={6} className="TextFeild gutter-row">
                        <Form.Item
                          name="lc_validity_complation_date"
                          label={
                            t("pl_lc_validity_complation_date") ||
                            "LC Validity Completion Date"
                          }
                          rules={[
                            {
                              required: true,
                              message:
                                t("val_postal_validity_complation") ||
                                "Please select validity completion date",
                            },
                          ]}
                        >
                          <DatePicker style={{ width: "100%" }} format="DD-MM-YY" />
                        </Form.Item>
                      </Col>
                    </Row>
                  )}
                </Panel>

                <Panel header="Vendor Document" key="5">
                  <Row gutter={16} align="bottom">
                    <Col xs={24} md={8}>
                      <Form.Item label="File Name" required>
                        <Input
                          prefix={<FileAlt />}
                          placeholder="Enter document name"
                          value={docNameInput}
                          onChange={(e) => setDocNameInput(e.target.value)}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={10}>
                      <Form.Item label="Upload Document">
                        <Upload
                          accept=".jpg,.jpeg,.png,.pdf"
                          showUploadList={false}
                          beforeUpload={(file) => {
                            setDocFile(file);
                            return false;
                          }}
                        >
                          <Button icon={<UploadOutlined />}>Upload Document</Button>
                        </Upload>
                        <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
                          Maximum file size: 2 MB (.jpg, .png, .pdf)
                          {docFile ? ` — selected: ${docFile.name}` : ""}
                        </div>
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={6}>
                      <Button type="primary" className="primary" onClick={handleAddDocumentRow}>
                        Send
                      </Button>
                    </Col>
                  </Row>
                  <Table
                    size="small"
                    rowKey="key"
                    columns={docColumns}
                    dataSource={documentRows}
                    locale={{ emptyText: "No Data" }}
                    pagination={false}
                    style={{ marginTop: 16 }}
                  />
                </Panel>
              </Collapse>

              <div className="subbuttonsrow" style={{ marginTop: 24 }}>
                <Button
                  type="primary"
                  className="primary"
                  icon={<SaveOutlined />}
                  loading={submitting}
                  htmlType="submit"
                >
                  {isEdit ? "Update vendor" : "Save vendor"}
                </Button>
              </div>
            </Form>
          </Spin>
        </div>
      </div>

      <Modal
        className="ModelPopupSec"
        title="Verify Email"
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        closable={handleCancel}
        wrapClassName="modal-wrapper"
        keyboard={false}
      >
        <VerifyOtpModal
          handleCancel={handleCancel}
          setVerifyEmailButtonView={setVerifyEmailButtonView}
          OTP={OTP}
          setOTP={setOTP}
          state={otpContactState}
          setState={patchOtpContactState}
          setMinutes={setMinutes}
          setSeconds={setSeconds}
          seconds={seconds}
          minutes={minutes}
          handleSendOtpEmail={handleSendOtpEmail}
          setVerificationResultEmail={setVerificationResultEmail}
        />
      </Modal>
      <Modal
        className="ModelPopupSec"
        title="Verify Mobile"
        open={isModalOpenPhone}
        onOk={handleOkPhone}
        onCancel={handleCancelPhone}
        closable={handleCancelPhone}
        wrapClassName="modal-wrapper"
        keyboard={false}
      >
        <PhoneOtpVerify
          handleCancelPhone={handleCancelPhone}
          setVerifyPhoneButtonView={setVerifyPhoneButtonView}
          OTP={OTPPhone}
          setOTP={setOTPPhone}
          state={otpContactState}
          setState={patchOtpContactState}
          setMinutes={setMinutesPhone}
          setSeconds={setSecondsPhone}
          seconds={secondsPhone}
          minutes={minutesPhone}
          handleSendOtpPhone={handleSendOtpPhone}
          setVerificationResultContact={setVerificationResultContact}
        />
      </Modal>
    </>
  );
};

export default GeneralVendorAdd;
